// @ts-strict-ignore
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { sprintf } from 'sprintf-js';
import styled, { css } from 'styled-components';

import { Box } from 'components/Box';
import { TrackedPrimaryButtonLink } from 'components/Button';
import { Hide } from 'components/Hide';
import { SocialIcon, SocialIconName } from 'components/Icon/Social';
import { Image } from 'components/Image';
import { LanguageLinks } from 'components/LanguageLinks';
import { RouterLink, TrackedLink } from 'components/Link';
import { IdealistLogoLegacy } from 'components/Logo';
import { BodySmall, H5 } from 'components/Text';
import { colors, gutterWidth, maxWidth, textStyles, transition } from 'theme';
import { AmpContext } from 'utils/AmpContext';
import { trackClicked } from 'utils/analytics';
import {
  LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE,
  LanguageLocale,
} from 'utils/constants';
import { getCurrentYear } from 'utils/date';
import { getHomeUrl } from 'utils/searchHomePages';

const linkStyles = css`
  display: block;
  color: ${colors.battleshipGrey};
  ${textStyles.bodySmall}
  &, * {
    line-height: 1.25;
  }
`;
const FooterLink = styled(
  ({ className, key, href, eventName, eventProperties, children }) =>
    typeof href === 'string' ? (
      <TrackedLink
        className={className}
        key={key}
        href={href}
        eventName={eventName}
        eventProperties={eventProperties}
      >
        {children}
      </TrackedLink>
    ) : (
      <RouterLink
        className={className}
        key={key}
        to={href}
        onClick={() => {
          trackClicked(eventName, eventProperties);
        }}
      >
        {children}
      </RouterLink>
    )
)`
  margin-right: 8px;
  margin-top: 1em;
  ${linkStyles}
`;
const SocialLink = styled(TrackedLink)`
  margin: 0 8px;
  border-radius: 50%;
  background: ${colors.battleshipGrey};
  transition: all ${transition};
  &:hover,
  &:focus {
    background: ${colors.hoverBlue};
  }
`;
const StyledLanguageLinks = styled(LanguageLinks)`
  margin: 0 12px;
  a,
  div {
    position: relative;
    margin: 0 12px;
    ${linkStyles}
    &:not(:first-child) {
      &::before {
        content: '';
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: -12px;
        height: 1em;
        width: 1px;
        background: currentColor;
        pointer-events: none;
      }
    }
  }
`;
type FooterLinkItem = {
  href:
    | string
    | {
        pathname: string;
        state?: Record<string, unknown>;
      };
  page: string;
  locales?: Array<string>;
};
type FooterLinkSection = {
  title: string;
  links: Array<FooterLinkItem>;
};
const NAV_MAP = {
  ...LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE,
  HOME: 'Idealist.org',
  GRAD_PROGRAM: getText('Grad Schools'),
  QUEUES: getText('Queues'),
  REPORTS: getText('Reports'),
  CREATE_PAYMENT: getText('Create Payment'),
  GRAD_FAIRS: getText('Grad Fairs'),
  EMAIL_PROMO: getText('Email Promos'),
  CMS: getText('CMS'),
};

function FooterLinks() {
  const { pathname } = useLocation();

  const footerLinks: Array<FooterLinkSection> = useMemo(
    () => [
      {
        title: getText('Discover'),
        links: ['JOB', 'INTERNSHIP', 'ORG', 'VOLOP', 'GRAD_PROGRAM']
          .map((listingType: DefaultSearchType) => ({
            href: getHomeUrl(listingType),
            page: NAV_MAP[listingType] || '',
          }))
          .concat([
            {
              href: getText('https://www.idealist.org/en/sitemap'),
              page: getText('Site Map'),
            },
          ]),
      },
      {
        title: getText('Post a Listing'),
        links: [
          {
            href: {
              pathname: '/post-a-listing',
              state: {
                from: pathname,
              },
            },
            page: getText('Post a Job'),
          },
          {
            href: getText('/organizations/create'),
            page: getText('Sign Up'),
          },
          {
            href: getText('/en/about/contact'),
            page: getText('Contact Us'),
          },
          {
            href: getText('/organizations'),
            page: getText('For Employers'),
          },
          {
            href: getText('/grad-schools/join'),
            page: getText('For Grad Schools'),
          },
        ],
      },
      {
        title: getText('Resources'),
        links: [
          {
            href: getText(
              'https://www.idealist.org/en/jobs/idealist-resources'
            ),
            page: getText('Job Seeker Tools'),
            locales: ['en'],
          },
          {
            href: getText('/en/careers'),
            page: getText('Career Advice'),
          },
          {
            href: getText('/grad-schools?pq='),
            page: getText('Grad Resources'),
            locales: ['en'],
          },
          {
            href: getText('/en/days/blog'),
            page: getText('Ideas for Action'),
          },
          {
            href: getText('http://blogidealistas.org/'),
            page: getText('Blog Idealistas'),
            locales: ['es'],
          },
          {
            href: getText('/en/days/logos-new'),
            page: getText('Logo & Design'),
          },
          {
            href: 'https://www.idealist.org/en/newsletter',
            page: 'Newsletter',
            locales: ['en'],
          },
        ],
      },
      {
        title: getText('Learn More'),
        links: [
          {
            href: getText('/en/about'),
            page: getText('About Us'),
          },
          {
            href: getText('/en/about/our-team'),
            page: getText('Our Team'),
          },
          {
            href: getText('/en/help'),
            page: getText('Help & FAQ'),
          },
          {
            href: getText('/en/about/contact'),
            page: getText('Contact Us'),
          },
          {
            href: getText('/en/terms-of-service'),
            page: getText('Terms of Service'),
          },
          {
            href: getText('/en/privacy-policy'),
            page: getText('Privacy Policy'),
          },
        ],
      },
    ],
    [pathname]
  );
  return (
    <>
      {footerLinks.map((section, index) => (
        <Box
          width={[1, 1 / 2, 1 / 4]}
          flex="0 0 auto"
          key={section.title}
          mt={index > 0 ? [32, null, 0] : undefined}
          display={index <= 1 ? ['none', null, 'block'] : 'block'}
        >
          <H5 mb={24}>{section.title}</H5>
          {section.links
            .filter(
              ({ locales }) => !locales || locales.includes(CURRENT_LOCALE)
            )
            .map(({ href, page }) => (
              <FooterLink
                href={href}
                key={page}
                eventName="Resource Page"
                eventProperties={{
                  page,
                }}
              >
                {page}
              </FooterLink>
            ))}
        </Box>
      ))}
    </>
  );
}

const socialLinks: { href: string; eventName: string; name: SocialIconName }[] =
  [
    {
      href: getText('https://www.facebook.com/Idealist'),
      eventName: 'Follow Idealist',
      name: 'facebook',
    },
    {
      href: getText('https://twitter.com/idealist'),
      eventName: 'Follow Idealist',
      name: 'twitter',
    },
    {
      href: getText('https://www.youtube.com/idealist'),
      eventName: 'Follow Idealist',
      name: 'youtube',
    },
    {
      href: getText('https://www.instagram.com/idealist_org/'),
      eventName: 'Follow Idealist',
      name: 'instagram',
    },
  ];

function SocialLinks() {
  return (
    <>
      {socialLinks.map(({ href, eventName, name }) => (
        <SocialLink
          href={href}
          key={name}
          eventName={eventName}
          aria-label={eventName}
        >
          <Box
            width={32}
            height={32}
            classes={['flex', 'justify-center', 'align-center']}
          >
            <SocialIcon name={name} width={20} height={20} fill="white" />
          </Box>
        </SocialLink>
      ))}
    </>
  );
}

function MissionStatement() {
  return (
    <BodySmall
      color={colors.battleshipGrey}
      textAlign={['center', null, null, 'left']}
      lineHeight={1.5}
    >
      {
        /* Mission statement under logo */
        getText(`Working with others, in a spirit of generosity and mutual respect, we want to
      help build a world where all people can lead free and dignified lives.`)
      }
    </BodySmall>
  );
}

type Props = {
  url: Record<LanguageLocale, string | undefined>;
  inPageHeader?: boolean;
  hideDivider?: boolean;
};

export function PageFooter({ url, inPageHeader, hideDivider }: Props) {
  return (
    <Box
      component="footer"
      data-qa-id={inPageHeader ? undefined : 'footer'}
      m="0 auto"
      maxWidth={maxWidth[3]}
      px={[gutterWidth / 2, null, null, gutterWidth]}
      bg={inPageHeader ? colors.platinum : 'white'}
      borderTop={hideDivider ? null : `1px solid ${colors.aluminum}`}
    >
      <Box display={inPageHeader ? undefined : ['none', null, null, 'block']}>
        <Box
          classes={['flex']}
          flexDirection={['column-reverse', null, null, 'row']}
          py={[gutterWidth / 2, null, null, gutterWidth]}
          pt={hideDivider ? 0 : null}
        >
          <Box
            flexDirection="column"
            width={[1, null, null, 3 / 10]}
            mt={[gutterWidth, null, null, 0]}
            classes={['flex', 'justify-between']}
            borderRight={`1px solid ${colors.aluminum}`}
            mr={5}
          >
            <Hide xs sm md>
              <TrackedLink eventName="Footer Logo" href="/">
                <AmpContext.Consumer>
                  {(requestIsAmp) =>
                    requestIsAmp ? (
                      <Image
                        src="/static/images/logo.png"
                        srcSet="/static/images/logo.png, /static/images/logo@2x.png 2x, /static/images/logo@3x.png 3x"
                        width={178}
                        height={56}
                        alt="Idealist.org"
                      />
                    ) : (
                      <IdealistLogoLegacy height={56} />
                    )
                  }
                </AmpContext.Consumer>
              </TrackedLink>
              <Box mb={4} mt={4} width={0.85}>
                <MissionStatement />
              </Box>
              <Box
                mr={[0, null, null, 24]}
                mb={[24, null, null, 0]}
                width={[1, null, null, 'auto']}
                flex="0 0 auto"
                classes={['flex']}
              >
                <TrackedPrimaryButtonLink
                  href={getText('/donate')}
                  size="large"
                  eventName="Donate - Footer"
                >
                  {getText('Donate to Idealist')}
                </TrackedPrimaryButtonLink>
              </Box>
            </Hide>
          </Box>
          <Box width={[null, null, null, 7 / 10]}>
            <Box display={['block', 'flex']} flexWrap="wrap">
              <FooterLinks />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        classes={['flex', 'justify-between', 'align-center']}
        flexWrap="wrap"
        borderTop={inPageHeader ? `1px solid ${colors.aluminum}` : null}
      >
        <Box
          classes={['justify-center']}
          display={['flex', null, null, 'none']}
          width={1}
          flexWrap="wrap"
          flex="0 0 auto"
          my={48}
        >
          <Box mx={12}>
            <Box
              classes={['flex', 'justify-center']}
              component={StyledLanguageLinks}
              url={url}
              showIcon={false}
            />
            <Box
              classes={['flex']}
              justifyContent={['center', null, null, 'flex-start']}
              flex="0 0 auto"
              width={[1, null, null, 1 / 2]}
              mt={[12, null, null, 0]}
            >
              <BodySmall
                color={colors.battleshipGrey}
                textAlign={['center', null, null, 'left']}
              >
                {sprintf(getText('Copyright © %(year)s Idealist.org'), {
                  year: getCurrentYear(),
                })}
              </BodySmall>
            </Box>
          </Box>
          <Box
            classes={['flex', 'align-center']}
            justifyContent={['center', null, null, 'flex-end']}
            textAlign={['center', null, null, 'right']}
            flexWrap="wrap"
            flex="0 0 auto"
            width={[1, null, null, 1 / 2]}
            mx={[0, null, null, -12]}
            mt={[24, null, null, 0]}
          >
            <Box mb={4} width={0.85}>
              <MissionStatement />
            </Box>
            <Box
              mr={[0, null, null, 24]}
              mb={[24, null, null, 0]}
              width={[1, null, null, 'auto']}
              flex="0 0 auto"
              classes={['flex', 'justify-center']}
            >
              <TrackedPrimaryButtonLink
                href={getText('/donate')}
                size="large"
                eventName="Donate - Footer"
              >
                {getText('Donate to Idealist')}
              </TrackedPrimaryButtonLink>
            </Box>

            <SocialLinks />
          </Box>
        </Box>
      </Box>

      <Box display={inPageHeader ? undefined : ['none', null, null, 'block']}>
        <Hide xs sm md>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            pt={[60 - gutterWidth / 2, null, null, 60 - gutterWidth]}
            pb={gutterWidth}
          >
            <Box display="flex" flex={1} justifyContent="flex-start">
              <SocialLinks />
            </Box>

            <BodySmall color={colors.battleshipGrey}>
              {sprintf(getText('Copyright © %(year)s Idealist.org'), {
                year: getCurrentYear(),
              })}
            </BodySmall>

            <Box display="flex" flex={1} mx={-12} justifyContent="flex-end">
              <Box
                classes={['flex', 'justify-center']}
                justifyContent={['left', null, null, 'flex-end']}
                component={StyledLanguageLinks}
                url={url}
                showIcon={false}
              />
            </Box>
          </Box>
        </Hide>
      </Box>
    </Box>
  );
}
